import { useEffect, useEffectEvent, useState } from 'react';

import { Dialog as DialogPrimitive } from 'radix-ui';
import { useTranslation } from 'react-i18next';

import { PinPad } from '@/shared/components/security/PinPad';
import { formatPhone } from '@/shared/lib/phone';
import { isTrivialPin, PIN_LENGTH } from '@/shared/lib/pin';
import { errorCode, errorMessage } from '@/shared/services/api/api-error';
import { api } from '@/shared/services/api/endpoints';
import type { Elevation } from '@/shared/services/api/types';
import { elevation } from '@/shared/services/auth/elevation';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/ui/cn';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';

import { usePinStore } from '@/stores/pin.store';
import type { PinFlow } from '@/stores/pin.store';
import { useMe, useSessionStore } from '@/stores/session.store';

/** Le code reçu par SMS compte six chiffres, comme celui de la connexion. */
const OTP_LENGTH = 6;

/**
 * Ce que la fenêtre attend à cet instant.
 *
 *  - `enter`   : le code, pour passer ;
 *  - `current` : le code actuel, avant d'en changer ;
 *  - `otp`     : le code reçu par SMS, quand le code est oublié ;
 *  - `choose`  : le nouveau code ;
 *  - `confirm` : le même, une seconde fois.
 */
type Step = 'enter' | 'current' | 'otp' | 'choose' | 'confirm';

const FIRST_STEP: Record<PinFlow, Step> = {
  verify: 'enter',
  create: 'choose',
  change: 'current',
  reset: 'otp',
};

const TITLE_KEYS = {
  enter: 'pin.title.enter',
  current: 'pin.title.current',
  otp: 'pin.title.otp',
  choose: 'pin.title.choose',
  confirm: 'pin.title.confirm',
} as const satisfies Record<Step, string>;

const HINT_KEYS = {
  enter: 'pin.hint.enter',
  current: 'pin.hint.current',
  otp: 'pin.hint.otp',
  choose: 'pin.hint.choose',
  confirm: 'pin.hint.confirm',
} as const satisfies Record<Step, string>;

/**
 * La fenêtre du code de confidentialité, posée une fois à la racine.
 *
 * Elle s'ouvre de deux façons, et l'appelant ne voit pas la différence : une
 * page d'argent la demande en s'ouvrant (`useElevationGate`), ou le serveur
 * refuse un geste faute de preuve et le client HTTP la demande à sa place.
 * Dans les deux cas, la réponse est une promesse : le code est passé, ou non.
 *
 * Le code ne reste nulle part dans le navigateur. Chaque saisie part au
 * serveur, qui compte les essais ; le site ne garde que la preuve rendue, en
 * mémoire, dix minutes. Plein écran sur téléphone, fenêtre centrée ailleurs.
 */
export function PinGate() {
  const request = usePinStore((state) => state.request);

  // Branché une fois : c'est par là que le client HTTP demande le code quand le
  // serveur refuse un geste faute de preuve.
  useEffect(() => {
    elevation.onChallenge(() => usePinStore.getState().open());
    return () => elevation.onChallenge(null);
  }, []);

  if (!request) {
    return null;
  }
  // La clé remonte la fenêtre à chaque changement de parcours : « code oublié »
  // repart d'une saisie vide, sans rien hériter de la précédente.
  return <PinDialog key={request.flow} flow={request.flow} />;
}

function PinDialog({ flow }: { flow: PinFlow }) {
  const { t } = useTranslation();
  const me = useMe();
  const close = usePinStore((state) => state.close);
  const switchTo = usePinStore((state) => state.switchTo);
  const sms = usePinStore((state) => state.sms);
  const sendResetSms = usePinStore((state) => state.sendResetSms);
  const forgetDevCode = usePinStore((state) => state.forgetDevCode);

  const [reached, setReached] = useState<Step>(FIRST_STEP[flow]);
  const [value, setValue] = useState('');
  /** Ce qui a déjà été saisi aux étapes précédentes du parcours. */
  const [kept, setKept] = useState<{ current?: string; otp?: string; chosen?: string }>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Minutes de blocage restantes, quand la saisie est fermée. */
  const [lockedFor, setLockedFor] = useState<number | null>(null);
  /** Chaque refus fait trembler la rangée de points : la clé rejoue l'animation. */
  const [shakes, setShakes] = useState(0);

  // Sans passerelle SMS (développement), le serveur rend le code : l'étape du
  // SMS n'a alors rien à demander, on passe au choix du nouveau code.
  const step: Step = reached === 'otp' && sms.devCode ? 'choose' : reached;
  const length = step === 'otp' ? OTP_LENGTH : PIN_LENGTH;

  /** Refus : les points tremblent, la saisie repart de zéro. */
  const refuse = (message: string) => {
    setError(message);
    setValue('');
    setShakes((count) => count + 1);
  };

  const succeed = (proof: Elevation) => {
    elevation.adopt(proof);
    close(true);
  };

  /** Le serveur a refusé le code : on dit les essais restants, ou le blocage. */
  const explain = async (failure: unknown) => {
    const code = errorCode(failure);
    if (code === 'PIN_INVALID' || code === 'PIN_LOCKED') {
      const status = await api.pin.status().catch(() => null);
      if (status?.lockedUntil) {
        const minutes = Math.max(
          1,
          Math.ceil((new Date(status.lockedUntil).getTime() - Date.now()) / 60_000),
        );
        setLockedFor(minutes);
        refuse(t('pin.locked', { minutes }));
        return;
      }
      if (status) {
        refuse(t('pin.wrong', { count: status.attemptsLeft }));
        return;
      }
    }
    refuse(errorMessage(failure));
  };

  /** Le compte vient de changer (il a un code) : la session le relit. */
  const refreshSession = async () => {
    try {
      const [account, permissions] = await Promise.all([api.auth.me(), api.auth.permissions()]);
      useSessionStore.getState().refreshMe(account, permissions.rules);
    } catch {
      // La session se remettra à jour au prochain démarrage : le code, lui,
      // est déjà enregistré.
    }
  };

  const submit = async (entered: string) => {
    setError(null);

    if (step === 'choose') {
      if (isTrivialPin(entered)) {
        refuse(t('pin.tooSimple'));
        return;
      }
      setKept((previous) => ({ ...previous, chosen: entered }));
      setValue('');
      setReached('confirm');
      return;
    }

    if (step === 'otp') {
      // Le code SMS se vérifie avec le nouveau code, en une seule requête : on
      // le garde et on passe au choix.
      setKept((previous) => ({ ...previous, otp: entered }));
      setValue('');
      setReached('choose');
      return;
    }

    setBusy(true);
    try {
      if (step === 'enter') {
        succeed(await api.pin.verify(entered));
        return;
      }
      if (step === 'current') {
        elevation.adopt(await api.pin.verify(entered));
        setKept((previous) => ({ ...previous, current: entered }));
        setValue('');
        setReached('choose');
        return;
      }

      // Dernière étape : la confirmation.
      if (entered !== kept.chosen) {
        setKept((previous) => ({ ...previous, chosen: undefined }));
        setReached('choose');
        refuse(t('pin.mismatch'));
        return;
      }
      const proof =
        flow === 'reset'
          ? await api.pin.reset(kept.otp ?? sms.devCode ?? '', entered)
          : await api.pin.set(entered, flow === 'change' ? kept.current : undefined);
      await refreshSession();
      succeed(proof);
    } catch (failure) {
      if (flow === 'reset' && step === 'confirm') {
        // Le code SMS était faux ou périmé : on y revient, le choix est à
        // refaire, et le code rendu par le serveur ne sert plus.
        forgetDevCode();
        setKept({});
        setReached('otp');
        refuse(t('pin.otpInvalid'));
        return;
      }
      await explain(failure);
    } finally {
      setBusy(false);
    }
  };

  const locked = lockedFor !== null && (step === 'enter' || step === 'current');
  const disabled = busy || locked || sms.status === 'sending';

  const press = (digit: string) => {
    if (disabled || value.length >= length) {
      return;
    }
    const next = value + digit;
    setValue(next);
    setError(null);
    if (next.length === length) {
      void submit(next);
    }
  };

  const erase = () => {
    setValue((current) => current.slice(0, -1));
    setError(null);
  };

  // Sur ordinateur, le clavier physique tape les mêmes chiffres que le pavé.
  const onKey = useEffectEvent((event: KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      press(event.key);
    } else if (event.key === 'Backspace') {
      event.preventDefault();
      erase();
    }
  });
  useEffect(() => {
    const listener = (event: KeyboardEvent) => onKey(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  const creating = flow === 'create' && step === 'choose';
  // À l'étape du SMS, l'échec de l'envoi prend la place du message de saisie.
  const shown = error ?? (step === 'otp' ? sms.error : null);

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => (open ? undefined : close(false))}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] hidden animate-fade-in bg-backdrop md:block" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-0 z-[60] flex animate-sheet-up flex-col bg-bg pt-2 pb-6 outline-none',
            'md:inset-auto md:top-1/2 md:left-1/2 md:max-h-[min(92dvh,720px)] md:w-[400px] md:-translate-x-1/2 md:-translate-y-1/2',
            'md:animate-scale-in md:rounded-lg md:bg-raised md:shadow-popover',
          )}
        >
          <div className="px-3">
            <IconButton name="close" label={t('action.close')} onClick={() => close(false)} />
          </div>

          <div className="flex flex-1 flex-col items-center justify-center px-6 md:py-4">
            <Icon name="lock" size={26} className="text-fg-strong" />
            <DialogPrimitive.Title className="mt-3 text-center text-title1 text-fg">
              {creating ? t('pin.title.create') : t(TITLE_KEYS[step])}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="mt-2 text-center text-subtle text-fg-muted">
              {creating
                ? t('pin.hint.create')
                : step === 'otp'
                  ? t(HINT_KEYS.otp, { phone: me?.phone ? formatPhone(me.phone) : '' })
                  : t(HINT_KEYS[step])}
            </DialogPrimitive.Description>

            <div
              key={shakes}
              role="progressbar"
              aria-label={t('pin.progress', { count: value.length, total: length })}
              aria-valuemin={0}
              aria-valuemax={length}
              aria-valuenow={value.length}
              className={cn('mt-8 flex gap-4', shakes > 0 && 'animate-shake')}
            >
              {Array.from({ length }, (_, index) => (
                <span
                  // Les points n'ont pas d'identité propre : leur rang est leur clé.
                  key={index}
                  className={cn(
                    'size-3.5 rounded-pill border-[1.5px] border-line',
                    // Le point rempli est l'élément actif de la fenêtre : il porte l'or.
                    index < value.length && 'border-accent bg-accent',
                    shown !== null && 'border-attention-ring',
                  )}
                />
              ))}
            </div>

            {/* Hauteur réservée : le message ne pousse pas le pavé quand il apparaît. */}
            <div className="mt-3 flex min-h-9 items-center justify-center">
              {shown ? (
                <p role="alert" className="text-center text-caption text-attention">
                  {shown}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <PinPad onDigit={press} onErase={erase} disabled={disabled} />

            <div className="flex min-h-9 justify-center">
              {step === 'enter' || step === 'current' ? (
                <Button
                  variant={locked ? 'outline' : 'quiet'}
                  size="sm"
                  onClick={() => switchTo('reset')}
                >
                  {locked ? t('pin.resetBySms') : t('pin.forgot')}
                </Button>
              ) : null}
              {step === 'otp' && sms.status !== 'sending' ? (
                <Button variant="quiet" size="sm" onClick={() => void sendResetSms()}>
                  {t('pin.resend')}
                </Button>
              ) : null}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
